// ---- Item grant dev panel (F8) ---------------------------------------------
// Dev-only bag cheats: pick any content item and shove N into the bag (or spill
// overflow on the ground). Sends `devGrant`; the server only honors it when it
// booted with DEV_COMMANDS=1 (`welcome.devCommands` — same gate as /map, /give).
//
// F-key map (don't steal these):
//   F3 interest HUD · F4 weather · F6 toon · F7 camera · F8 THIS panel
//
// Follows weatherdev.ts / toondev.ts panel scaffolding.

import { ITEMS } from "./items/index.ts";
import { sendDevGrant, serverDevCommands } from "./net.ts";

let panel: HTMLDivElement;
let listEl: HTMLDivElement;
let notice: HTMLDivElement;
let filterEl: HTMLInputElement;
let countEl: HTMLInputElement;
let on = false;
let filter = "";

const BTN =
  "font:11px ui-monospace, Menlo, monospace; color:#e3d8cf; background:#241c18;" +
  "border:1px solid #4a3a32; border-radius:5px; padding:3px 8px; cursor:pointer; text-align:left;";

function grantCount(): number {
  const n = Math.floor(Number(countEl?.value));
  return Number.isFinite(n) && n > 0 ? Math.min(999, n) : 1;
}

function grant(type: string): void {
  if (!serverDevCommands) return;
  sendDevGrant(type, grantCount());
}

function rebuildList(): void {
  if (!listEl) return;
  listEl.replaceChildren();
  const q = filter.trim().toLowerCase();
  const rows = ITEMS
    .filter((s) => {
      if (!q) return true;
      return s.type.includes(q) || s.name.toLowerCase().includes(q) || (s.icon ?? "").includes(q);
    })
    .slice()
    .sort((a, b) => a.name.localeCompare(b.name));

  if (!rows.length) {
    const empty = document.createElement("div");
    empty.textContent = "no matches";
    empty.style.cssText = "color:#8a7a6a; padding:6px 2px;";
    listEl.appendChild(empty);
    return;
  }

  for (const s of rows) {
    const b = document.createElement("button");
    b.type = "button";
    b.style.cssText = BTN + "width:100%; display:flex; gap:6px; align-items:center;";
    b.innerHTML =
      `<span style="font-size:16px;line-height:1;width:22px;text-align:center">${s.icon ?? "·"}</span>` +
      `<span style="flex:1;overflow:hidden;text-overflow:ellipsis;white-space:nowrap">${s.name}</span>` +
      `<span style="color:#8a7a6a;font-size:10px">${s.type}</span>`;
    b.title = `Grant ${s.type} ×${grantCount()}`;
    b.onclick = () => grant(s.type);
    listEl.appendChild(b);
  }
}

function syncNotice(): void {
  if (!notice) return;
  if (!serverDevCommands) {
    notice.textContent = "server booted without DEV_COMMANDS=1 — grants are ignored";
    notice.style.display = "block";
  } else {
    notice.textContent = "";
    notice.style.display = "none";
  }
}

export function initGrantDev(): void {
  panel = document.createElement("div");
  panel.id = "grantdev";
  panel.style.cssText =
    "position:fixed; top:14px; left:12px; z-index:7; display:none; width:300px;" +
    "font:11px/1.45 ui-monospace, Menlo, monospace; color:#e3d8cf;" +
    "background:rgba(18,14,12,.92); border:1px solid #4a3a32; border-radius:8px;" +
    "padding:8px 10px; user-select:none; max-height:min(70vh,520px);" +
    "display:none; flex-direction:column; gap:6px;";
  panel.addEventListener("pointerdown", (e) => e.stopPropagation());
  // Keep typing in the filter from driving game movement / chat.
  panel.addEventListener("keydown", (e) => e.stopPropagation());
  panel.addEventListener("keyup", (e) => e.stopPropagation());

  const title = document.createElement("div");
  title.textContent = "🧰 grant item (F8)";
  title.style.cssText = "font-weight:600;";
  panel.appendChild(title);

  notice = document.createElement("div");
  notice.style.cssText = "color:#c9a05a; white-space:pre-wrap;";
  panel.appendChild(notice);

  const tools = document.createElement("div");
  tools.style.cssText = "display:flex; gap:6px; align-items:center;";
  filterEl = document.createElement("input");
  filterEl.type = "search";
  filterEl.placeholder = "filter…";
  filterEl.autocomplete = "off";
  filterEl.spellcheck = false;
  filterEl.style.cssText =
    "flex:1; min-width:0; font:11px ui-monospace,monospace; color:#e3d8cf;" +
    "background:#1a1410; border:1px solid #4a3a32; border-radius:5px; padding:4px 6px;";
  filterEl.oninput = () => {
    filter = filterEl.value;
    rebuildList();
  };
  const countLab = document.createElement("span");
  countLab.textContent = "×";
  countLab.style.cssText = "color:#8a7a6a;";
  countEl = document.createElement("input");
  countEl.type = "number";
  countEl.min = "1";
  countEl.max = "999";
  countEl.value = "1";
  countEl.title = "How many to grant per click";
  countEl.style.cssText =
    "width:48px; font:11px ui-monospace,monospace; color:#e3d8cf;" +
    "background:#1a1410; border:1px solid #4a3a32; border-radius:5px; padding:4px 4px;";
  tools.append(filterEl, countLab, countEl);
  panel.appendChild(tools);

  const quick = document.createElement("div");
  quick.style.cssText = "display:flex; flex-wrap:wrap; gap:4px;";
  for (const n of [1, 10, 50]) {
    const b = document.createElement("button");
    b.type = "button";
    b.textContent = `×${n}`;
    b.style.cssText = BTN;
    b.onclick = () => { countEl.value = String(n); };
    quick.appendChild(b);
  }
  const tip = document.createElement("span");
  tip.textContent = "click a row · /give type [n]";
  tip.style.cssText = "color:#8a7a6a; margin-left:4px;";
  quick.appendChild(tip);
  panel.appendChild(quick);

  listEl = document.createElement("div");
  listEl.style.cssText =
    "overflow:auto; flex:1; min-height:120px; display:flex; flex-direction:column; gap:3px;" +
    "padding-right:2px;";
  panel.appendChild(listEl);

  document.body.appendChild(panel);
  rebuildList();
  // F8 is wired in input.ts (with the other F-key toggles).
}

export function toggleGrantDev(force?: boolean): void {
  on = force ?? !on;
  panel.style.display = on ? "flex" : "none";
  if (on) {
    syncNotice();
    rebuildList();
    // Focus filter for quick type-ahead once the panel is up.
    queue-microtask(() => filterEl?.focus());
  }
}

export function isGrantDevOpen(): boolean {
  return on;
}
